import { useEffect, useMemo, useState } from "react";
import { Alert, Box, CardActionArea, Typography, useTheme } from "@mui/material";
import moment from "moment";
import { LegendaContainer, MesaCard, MesasComponentContainer } from "./styles";
import { IConfiguracaoPedidos } from "../../../../interface/IConfiguracaoPedidos";
import { IPedido } from "../../../../interface/IPedido";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { QrCode2, ReceiptLong } from "@mui/icons-material";
import { useMesasQRCode } from "../../../../hooks/useMesasQRCode";
import { ISessaoMesa, StatusSessaoMesa } from "../../../../interface/ISessaoMesa";
import { StatusRodadaPedido } from "../../../../enum/StatusRodadaPedido";

interface MesasContainerProps {
    configuracoes: IConfiguracaoPedidos | null | undefined;
    selecionarMesa: (id: number) => void;
    mesaId?: number;
    pedidos: IPedido[];
    sm: number;
    tools?: React.ReactNode;
}

type EstadoMesa = "livre" | "ocupada" | "selecionada";

/** Tempo de mesa aberta, na leitura que o salão usa: "32 min", "1h 06". */
const formatarTempo = (dataHora?: string): string => {
    if (!dataHora) return "";

    const minutos = Math.max(0, moment().diff(moment(dataHora), "minutes"));
    if (minutos < 60) return `${minutos} min`;

    const horas = Math.floor(minutos / 60);
    return `${horas}h ${String(minutos % 60).padStart(2, "0")}`;
};

/**
 * Mapa de mesas do salão.
 *
 * Cada ladrilho carrega o que o gerente olha de fato: o número, há quanto tempo
 * a mesa está aberta e quanto a comanda já acumulou. Antes eram quadrados
 * chapados só com o número — dava para contar mesas ocupadas, mas não para
 * decidir qual atender primeiro.
 */
export const MesasContainer = ({ configuracoes, selecionarMesa, mesaId, pedidos, tools }: MesasContainerProps) => {
    const { real } = useMoeda();
    const theme = useTheme();

    // O tempo de mesa precisa envelhecer sozinho: sem este tique, "12 min"
    // ficava congelado até alguém mexer em outra coisa da tela.
    const [, setTique] = useState(0);
    useEffect(() => {
        const intervalo = setInterval(() => setTique(valor => valor + 1), 30000);
        return () => clearInterval(intervalo);
    }, []);

    // Sessões do QR Code (quem está na mesa, conta pedida). Recarrega junto com os pedidos,
    // que já são atualizados pelo WebSocket — inclusive pelos eventos da mesa.
    const { listarSessoes } = useMesasQRCode();
    const [sessoes, setSessoes] = useState<ISessaoMesa[]>([]);
    useEffect(() => {
        listarSessoes().then(setSessoes);
    }, [pedidos]);

    const sessaoPorMesa = useMemo(() => {
        const mapa = new Map<number, ISessaoMesa>();
        sessoes.forEach(sessao => mapa.set(sessao.mesaId, sessao));
        return mapa;
    }, [sessoes]);

    /** Pedido aberto de cada mesa, indexado pelo número da mesa. */
    const pedidoPorMesa = useMemo(() => {
        const mapa = new Map<number, IPedido>();
        pedidos?.forEach(pedido => {
            if (pedido.mesaId) {
                mapa.set(pedido.mesaId, pedido);
            }
        });
        return mapa;
    }, [pedidos]);

    const totalMesas = configuracoes?.numeroMesas ?? 0;
    const ocupadas = pedidoPorMesa.size;

    const corDoEstado: Record<EstadoMesa, string> = {
        livre: theme.palette.success.main,
        ocupada: theme.palette.primary.main,
        selecionada: theme.palette.secondary.main,
    };

    const LEGENDA: { estado: EstadoMesa; rotulo: string }[] = [
        { estado: "livre", rotulo: "Livre" },
        { estado: "ocupada", rotulo: "Ocupada" },
        { estado: "selecionada", rotulo: "Selecionada" },
    ];

    return (
        <MesasComponentContainer>
            {tools && (
                <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "background.paper", px: 2 }}>
                    {tools}
                </Box>
            )}

            {/* Cabeçalho com a contagem e a legenda de cores */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    px: 2,
                    pb: 1.5,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                    }}
                >
                    Salão · {totalMesas} {totalMesas === 1 ? "mesa" : "mesas"}
                    {ocupadas > 0 && ` · ${ocupadas} ocupada${ocupadas > 1 ? "s" : ""}`}
                </Typography>

                <LegendaContainer>
                    {LEGENDA.map(({ estado, rotulo }) => (
                        <Box key={estado} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: corDoEstado[estado] }} />
                            <Typography sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>
                                {rotulo}
                            </Typography>
                        </Box>
                    ))}
                </LegendaContainer>
            </Box>

            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    overflowY: "auto",
                }}
            >
                {totalMesas ? (
                    <Box
                        sx={{
                            display: "grid",
                            gap: 1.5,
                            gridTemplateColumns: {
                                xs: "repeat(auto-fill, minmax(104px, 1fr))",
                                sm: "repeat(auto-fill, minmax(120px, 1fr))",
                                md: "repeat(auto-fill, minmax(136px, 1fr))",
                            },
                            width: "100%",
                            alignContent: "flex-start",
                        }}
                    >
                        {[...Array(totalMesas)].map((_, index) => {
                            const mesaNumero = index + 1;
                            const pedido = pedidoPorMesa.get(mesaNumero);
                            const sessao = sessaoPorMesa.get(mesaNumero);
                            const contaSolicitada = sessao?.status === StatusSessaoMesa.CONTA_SOLICITADA;
                            const rodadasAguardando = pedido?.rodadas?.filter(
                                rodada => rodada.status === StatusRodadaPedido.AGUARDANDO_CONFIRMACAO
                            ).length ?? 0;
                            const estado: EstadoMesa =
                                mesaNumero === mesaId ? "selecionada" : pedido ? "ocupada" : "livre";
                            const cor = corDoEstado[estado];

                            return (
                                <MesaCard key={mesaNumero} variant="outlined" estado={estado} elevation={0}>
                                    <CardActionArea
                                        onClick={() => selecionarMesa(mesaNumero)}
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "stretch",
                                            justifyContent: "space-between",
                                            p: 1.25,
                                            borderRadius: "12px",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 18,
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                    color: pedido || estado === "selecionada" ? "text.primary" : "text.disabled",
                                                }}
                                            >
                                                {mesaNumero}
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                {sessao && (
                                                    <QrCode2
                                                        titleAccess={`${sessao.participantes.length} pessoa(s) pelo QR Code`}
                                                        sx={{ fontSize: 16, color: "text.secondary" }}
                                                    />
                                                )}
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        backgroundColor: cor,
                                                        opacity: estado === "livre" ? 0.5 : 1,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {(contaSolicitada || rodadasAguardando > 0) && (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                                                {contaSolicitada && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
                                                        <ReceiptLong sx={{ fontSize: 14 }} />
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Pediu a conta</Typography>
                                                    </Box>
                                                )}
                                                {rodadasAguardando > 0 && (
                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "info.main", textAlign: "left" }}>
                                                        {rodadasAguardando} {rodadasAguardando === 1 ? "pedido" : "pedidos"} p/ confirmar
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        {pedido ? (
                                            <Box sx={{ textAlign: "left", mt: 1.5 }}>
                                                <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3 }}>
                                                    {formatarTempo(pedido.dataHora)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>
                                                    {real(pedido.valorTotal)}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontSize: 11, color: "text.disabled", textAlign: "left", mt: 1.5 }}>
                                                livre
                                            </Typography>
                                        )}
                                    </CardActionArea>
                                </MesaCard>
                            );
                        })}
                    </Box>
                ) : (
                    <Alert severity="info">Nenhuma mesa configurada</Alert>
                )}
            </Box>
        </MesasComponentContainer>
    );
};
